import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import _formatNumber from 'number-format.js'
import toSentenceCase from 'to-sentence-case'
import CreditCardMasterCard from 'design-system/Svgs/CreditCardMasterCard/source.svg?raw'
import CreditCardVisa from 'design-system/Svgs/CreditCardVisa/source.svg?raw'
import CreditCardDiscover from 'design-system/Svgs/CreditCardDiscover/source.svg?raw'
import CreditCardAmex from 'design-system/Svgs/CreditCardAmex/source.svg?raw'
import subDays from 'date-fns/subDays'

/**
 * @type {(input: {
 *   pdfMake: import('pdfmake/build/pdfmake'),
 *   statement: import('./types.d.ts').Statement,
 *   credit_cards: import('./types.d.ts').CreditCard[]
 * }) => ReturnType<import('pdfmake/build/pdfmake').createPdf>}
 */
export default function createStatementPdf({
  statement,
  credit_cards,
  pdfMake,
}) {
  /** @type {import('pdfmake/interfaces.js').TableCell[][]} */
  let transactions = statement.TRANSACTIONS.map(item => {
    return [
      {
        text: format(parseISO(item.date), 'MM/dd/yyyy'),
        alignment: 'left',
      },
      {
        text: `${toSentenceCase(item.type)}${
          item.human_id ? ` #${item.human_id}` : ''
        }`,
        alignment: 'left',
      },
      {
        text: numberMoney(item.amount),
        alignment: 'right',
      },
    ]
  })

  transactions.unshift([
    {
      text: '',
      alignment: 'left',
    },
    {
      text: `Previous balance as of ${format(subDays(parseISO(statement.TRANSACTIONS_START_DATE), 1), 'MM/dd/yyyy')}`,
      alignment: 'left',
    },
    {
      text: numberMoney(statement.DUE_PAST),
      alignment: 'right',
    },
  ])

  /** @type {import('pdfmake/interfaces.js').Content[]} */
  let content = [
    {
      columns: [
        {
          alignment: 'left',
          stack: [
            {
              margin: [24, -6, 0, 0],
              stack: [
                { text: statement.LOCATION_PARENT_NAME, bold: true },
                { text: statement.LOCATION_ADDRESS_LINE1 },
                {
                  text: `${statement.LOCATION_CITY}, ${statement.LOCATION_STATE} ${statement.LOCATION_ZIP}`,
                },
              ],
            },
          ],
        },
        {
          alignment: 'right',
          stack: [
            {
              margin: [0, -24, 32, 0],
              alignment: 'right',
              image: statement.LOCATION_LOGO_URL,
              fit: [100, 150],
            },
            {
              margin: [0, 12, 0, 0],
              columns: [
                {
                  margin: [0, 2, 0, 0],
                  text: 'Statement date',
                  bold: true,
                },
                {
                  margin: [24, 0, 24, 0],
                  layout: {
                    hLineColor: '#E4E7EB',
                    vLineColor: '#E4E7EB',
                  },
                  table: {
                    widths: [80],
                    body: [
                      [
                        {
                          text: format(
                            parseISO(statement.REFERENCE_DATE),
                            'MM/dd/yyyy'
                          ),
                          alignment: 'center',
                        },
                      ],
                    ],
                  },
                },
              ],
            },
            {
              columns: [
                {
                  margin: [0, 2, 0, 0],
                  text: 'Amount due now',
                  bold: true,
                },
                {
                  margin: [24, 0, 24, 0],
                  layout: {
                    hLineColor: '#E4E7EB',
                    vLineColor: '#E4E7EB',
                    hLineWidth: (i, node) => (i === 0 ? 0 : 1),
                  },
                  table: {
                    widths: [80],
                    body: [
                      [
                        {
                          text: numberMoney(statement.DUE_NOW),
                          alignment: 'center',
                        },
                      ],
                    ],
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    {
      columns: [
        {
          alignment: 'left',
          stack: [
            { text: 'BILL TO:', bold: true },

            {
              margin: [24, 12, 0, 0],
              stack: [
                {
                  text: `${statement.PAYER_FIRST_NAME} ${statement.PAYER_LAST_NAME}`,
                  bold: true,
                },
                { text: statement.PAYER_ADDRESS_LINE1 || '' },
                {
                  text:
                    statement.PAYER_CITY &&
                    statement.PAYER_STATE &&
                    statement.PAYER_ZIP
                      ? [
                          statement.PAYER_ADDRESS_LINE2,
                          `${statement.PAYER_CITY}, ${statement.PAYER_STATE} ${statement.PAYER_ZIP}`,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : '',
                },
              ],
            },
          ],
        },
        {
          alignment: 'right',
          margin: [0, 22, 0, 0],
          stack: [
            {
              columns: [
                {
                  text: 'Patient name',
                  bold: true,
                },
                {
                  text: `${statement.PATIENT_FIRST_NAME} ${statement.PATIENT_LAST_NAME}`,
                  alignment: 'left',
                  margin: [24, 0, 0, 0],
                },
              ],
            },
            {
              columns: [
                {
                  text: 'Pay online here',
                  bold: true,
                },
                {
                  text: 'hub.greyfinch.com',
                  link: 'https://hub.greyfinch.com',
                  style: {
                    decoration: 'underline',
                  },
                  alignment: 'left',
                  margin: [24, 0, 0, 0],
                },
              ],
            },
          ],
        },
      ],
    },

    {
      margin: [0, 24, 0, 0],
      layout: 'noBorders',
      table: {
        widths: ['*', '*', '*', '*', '*', '*'],
        body: [
          [
            { text: 'Account balance', style: 'tableHeader' },
            { text: '0-30 days', style: 'tableHeader' },
            { text: 'Over 30 days', style: 'tableHeader' },
            { text: 'Over 60 days', style: 'tableHeader' },
            { text: 'Over 90 days', style: 'tableHeader' },
            { text: 'Over 120 days', style: 'tableHeader' },
          ],
          [
            {
              text: numberMoney(statement.DUE_AMOUNT),
              style: 'tableCell',
            },
            {
              text: numberMoney(statement.DUE_0_30_DAYS),
              style: 'tableCell',
            },
            {
              text: numberMoney(statement.DUE_31_60_DAYS),
              style: 'tableCell',
            },
            {
              text: numberMoney(statement.DUE_61_90_DAYS),
              style: 'tableCell',
            },
            {
              text: numberMoney(statement.DUE_91_120_DAYS),
              style: 'tableCell',
            },
            {
              text: numberMoney(statement.DUE_MORE_120_DAYS),
              style: 'tableCell',
            },
          ],
        ],
      },
    },
  ]

  /**
   * Pagination logic for transactions table:
   *
   * Case 1 - Few transactions (≤10):
   *   Everything on one page: header + transactions + credit card slip
   *
   * Case 2 - Medium transactions (11-24):
   *   Page 1: Header + all transactions (no credit slip)
   *   Page 2: Credit card slip only
   *
   * Case 3 - Many transactions (>24):
   *   Page 1: Header + first 24 transactions
   *   Middle pages: Up to 35 transactions each
   *   Last page: Remaining transactions (≤24) + credit card slip
   *   Edge case: If remaining > 24, show 35 on that page, push credit slip to new page
   */

  let pageNumber = 1
  let totalTransactions = transactions.length
  /** @type {(transactions: import('pdfmake/interfaces.js').TableCell[][]) => import('pdfmake/interfaces.js').Content[]} */
  function transactionsTable(transactions) {
    return [
      {
        margin: [4, 16, 0, 0],
        text: `Transactions date range: ${format(parseISO(statement.TRANSACTIONS_START_DATE), 'MM/dd/yyyy')} - ${format(parseISO(statement.TRANSACTIONS_END_DATE), 'MM/dd/yyyy')}`,
      },
      {
        margin: [0, 4, 0, 0],
        layout: {
          hLineColor: '#E4E7EB',
          vLineColor: '#E4E7EB',
          hLineWidth: i => (i <= 1 ? 0 : 1),
          vLineWidth: () => 0,
        },
        table: {
          widths: [100, '*', 100],
          headerRows: 1,
          body: [
            [
              { text: 'Transaction date', style: 'tableHeaderLeft' },
              { text: 'Description', style: 'tableHeaderLeft' },
              { text: 'Amount', style: 'tableHeaderRight' },
            ],
            ...transactions,
          ],
        },
      },
    ]
  }

  // Case 1: Everything fits on one page
  if (totalTransactions <= TRANSACTIONS_FIT_WITH_HEADER_AND_SLIP) {
    content.push(...transactionsTable(transactions))
    transactions = []
  } else {
    // Case 2 & 3: Need multiple pages

    // First page: Header is already added, add up to 24 transactions
    let firstPageTransactions = transactions.slice(
      0,
      TRANSACTIONS_FIT_WITH_HEADER
    )
    content.push(...transactionsTable(firstPageTransactions))
    transactions = transactions.slice(firstPageTransactions.length)
    pageNumber++

    // Middle pages: Full pages of 35 transactions
    // Continue while we have more than can fit with the credit slip
    while (transactions.length > TRANSACTIONS_FIT_WITH_SLIP) {
      content.push({ text: '', pageBreak: 'before' })

      let pageTransactions = transactions.slice(0, TRANSACTIONS_ON_BLANK_PAGE)
      content.push(...transactionsTable(pageTransactions))
      transactions = transactions.slice(pageTransactions.length)
      pageNumber++
    }
  }

  // Last page: Remaining transactions (if any) + credit card slip
  // At this point, transactions.length is either 0 or ≤ TRANSACTIONS_FIT_WITH_SLIP

  if (transactions.length > 0) {
    // We have remaining transactions to show with the credit slip
    content.push({ text: '', pageBreak: 'before' })
    content.push(...transactionsTable(transactions))
  } else if (pageNumber > 1) {
    // No remaining transactions but we used multiple pages
    // Credit slip goes on new page
    content.push({ text: '', pageBreak: 'before' })
  }

  // Credit card slip
  content.push(
    {
      margin: [0, 24, 0, 0],
      absolutePosition: { x: 24, y: 500 },
      canvas: [
        {
          type: 'line',
          x1: -20,
          y1: 0,
          x2: 600,
          y2: 0,
          dash: { length: 5, space: 6 },
        },
      ],
    },
    {
      margin: [0, 24, 0, 0],
      absolutePosition: { x: 24, y: 520 },
      columns: [
        {
          stack: [
            {
              margin: [24, 0, 0, 0],
              stack: [
                {
                  text: `${statement.PAYER_FIRST_NAME} ${statement.PAYER_LAST_NAME}`,
                  bold: true,
                },
                { text: statement.PAYER_ADDRESS_LINE1 || '' },
                {
                  text:
                    statement.PAYER_CITY &&
                    statement.PAYER_STATE &&
                    statement.PAYER_ZIP
                      ? [
                          statement.PAYER_ADDRESS_LINE2,
                          `${statement.PAYER_CITY}, ${statement.PAYER_STATE} ${statement.PAYER_ZIP}`,
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : '',
                },
              ],
            },

            {
              text: 'MAKE CHECKS PAYABLE TO:',
              bold: true,
              margin: [0, 24, 0, 0],
            },

            {
              margin: [24, 12, 0, 0],

              stack: [
                { text: statement.LOCATION_PARENT_NAME, bold: true },
                { text: statement.LOCATION_ADDRESS_LINE1 },
                {
                  text: `${statement.LOCATION_CITY}, ${statement.LOCATION_STATE} ${statement.LOCATION_ZIP}`,
                },
              ],
            },
          ],
        },

        {
          width: '55%',
          stack: [
            {
              layout: {
                hLineColor: '#E4E7EB',
                vLineColor: '#E4E7EB',
              },
              table: {
                widths: ['auto', '*', 'auto', 'auto'],
                body: [
                  [
                    { text: 'Patient ID', style: 'tableHeader' },
                    { text: 'Patient name', style: 'tableHeader' },
                    { text: 'Amount', style: 'tableHeader' },
                    { text: 'Amount enclosed', style: 'tableHeader' },
                  ],
                  [
                    {
                      text: `#${statement.PATIENT_HUMAN_ID}`,
                      style: 'tableCell',
                    },
                    {
                      text: `${statement.PATIENT_FIRST_NAME} ${statement.PATIENT_LAST_NAME}`,
                      style: 'tableCell',
                    },
                    {
                      text: numberMoney(statement.DUE_NOW),
                      style: 'tableCell',
                    },
                    { text: '', style: 'tableCell' },
                  ],
                ],
              },
            },
            {
              margin: [0, 12, 0, 0],
              text: `If paying with ${toSentenceWithChoices(
                credit_cards
              )}, fill out below.`,
            },
            {
              margin: [0, 4, 0, 0],
              table: {
                body: [
                  [
                    credit_cards.includes('Visa')
                      ? {
                          svg: CreditCardVisa,
                          fit: [25, 25],
                          alignment: 'center',
                          colSpan: 2,
                        }
                      : { text: '', colSpan: 2 },
                    {},
                    credit_cards.includes('MasterCard')
                      ? {
                          svg: CreditCardMasterCard,
                          fit: [25, 25],
                          alignment: 'center',
                          colSpan: 2,
                        }
                      : { text: '', colSpan: 2 },
                    {},
                    credit_cards.includes('Amex')
                      ? {
                          svg: CreditCardAmex,
                          fit: [25, 25],
                          alignment: 'center',
                          colSpan: 2,
                        }
                      : { text: '', colSpan: 2 },
                    {},
                    credit_cards.includes('Discover')
                      ? {
                          svg: CreditCardDiscover,
                          fit: [25, 25],
                          alignment: 'center',
                          colSpan: 2,
                        }
                      : { text: '', colSpan: 2 },
                    {},
                  ],
                  [
                    {
                      stack: [
                        {
                          text: 'Card Number',
                        },
                        {
                          text: '     |     |     |     |     |     |     |     |     |     |     |     |     |     |     |     |     ',
                          fontSize: 12,
                        },
                      ],
                      colSpan: 8,
                    },
                    {},
                    {},
                    {},
                    {},
                    {},
                    {},
                    {},
                  ],
                  [
                    {
                      text: 'Signature:',
                      colSpan: 5,
                    },
                    {},
                    {},
                    {},
                    {},
                    {
                      text: 'Exp. Date:',
                      colSpan: 3,
                    },
                    {},
                    {},
                  ],
                  [
                    {
                      text: 'Statement Date:',
                      colSpan: 3,
                    },
                    {},
                    {},
                    {
                      text: 'CVV/CID:',
                      colSpan: 2,
                    },
                    {},
                    {
                      text: 'Zip Code:',
                      colSpan: 3,
                    },
                    {},
                    {},
                  ],
                  [
                    {
                      text: 'Pay This Amount',
                      colSpan: 4,
                    },
                    {},
                    {},
                    {},
                    {
                      stack: [
                        {
                          text: 'Show Amount ',
                        },
                        {
                          text: 'Paid Here $',
                        },
                      ],
                      colSpan: 4,
                    },
                    {},
                    {},
                    {},
                  ],
                ],
              },
            },
          ],
        },
      ],
    }
  )

  return pdfMake.createPdf({
    content,
    styles: {
      tableHeader: {
        bold: true,
        alignment: 'center',
        fillColor: '#E4E7EB',
      },
      tableHeaderLeft: {
        bold: true,
        alignment: 'left',
        fillColor: '#E4E7EB',
      },
      tableHeaderRight: {
        bold: true,
        alignment: 'right',
        fillColor: '#E4E7EB',
      },
      tableCell: {
        alignment: 'center',
      },
    },
    defaultStyle: {
      fontSize: 10,
    },
    footer: function (currentPage, pageCount) {
      return [
        {
          text: `Page ${currentPage} of ${pageCount}`,
          italics: true,
          alignment: 'right',
          margin: [0, 0, 24, 0],
          fontSize: 8,
        },
      ]
    },
    pageSize: 'LETTER',

    // by default we use portrait, you can change it to landscape if you wish
    pageOrientation: 'portrait',

    // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
    pageMargins: [24, 48],
  })
}

// duplicating the helper here because Data/format.js has too many things
// that break the web worker
function formatNumber(value, format) {
  return typeof value === 'number' ? _formatNumber(format, value) : value
}

function numberMoney(value) {
  return formatNumber(value, '$#,##0.00')
}

function toSentenceWithChoices(list) {
  return list.length > 1
    ? list.slice(0, -1).join(', ') + ', or ' + list.slice(-1)
    : list[0]
}

/**
 * Maximum transactions on a blank page (no header or slip)
 */
let TRANSACTIONS_ON_BLANK_PAGE = 38

/**
 * Maximum transactions that fit with header (no slip)
 */
let TRANSACTIONS_FIT_WITH_HEADER = 27

/**
 * Maximum transactions that fit with credit card slip (no header)
 */
let TRANSACTIONS_FIT_WITH_SLIP = 23

/**
 * Maximum transactions that fit with both header and credit card slip
 */
let TRANSACTIONS_FIT_WITH_HEADER_AND_SLIP = 11
